/**
 * Import/Export Sidebar Component
 * 
 * Handles importing and exporting collection data
 * Reference: reference/app/src/views/private/components/export-sidebar-detail.vue
 */

import { useState, useRef, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Download, Upload, FileText, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useQueryClient } from '@tanstack/react-query';
import type { ItemsQuery } from '@/stores/items';
import { FilterBuilder, type Filter } from '@/components/filters';
import { useFields } from '@/hooks/useFields';
import { Separator } from '@/components/ui/separator';
import { FieldSelector } from './FieldSelector';

// ── CSV/JSON client-side helpers ────────────────────────────────────────────

/**
 * Walk a dot-separated path through a (possibly nested) item.
 * If any segment is an array, map over it and join the results.
 */
function getNestedValue(obj: unknown, path: string): unknown {
    const parts = path.split('.');
    let current: unknown = obj;
    for (const part of parts) {
        if (current === null || current === undefined) return '';
        if (Array.isArray(current)) {
            // Array segment — recurse into each element for the remaining path
            const remaining = parts.slice(parts.indexOf(part)).join('.');
            return current.map((el) => getNestedValue(el, remaining)).join('; ');
        }
        current = (current as Record<string, unknown>)[part];
    }
    return current;
}

/** Serialize a single cell value for a CSV row. */
function csvCell(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) {
        // Flatten arrays to semicolon-separated strings
        return csvCell(value.map((v) => (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v ?? ''))).join('; '));
    }
    if (typeof value === 'object') return csvCell(JSON.stringify(value));
    const s = String(value);
    // Wrap in quotes if the value contains a comma, double-quote, or newline
    if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

function triggerDownload(content: string, filename: string, mime: string) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Delay revocation so the browser has time to read the Blob before the URL is invalidated.
    setTimeout(() => URL.revokeObjectURL(url), 250);
}

// ── Component ────────────────────────────────────────────────────────────────

interface ImportExportSidebarProps {
    collection: string;
    query?: ItemsQuery;
    onRefresh?: () => void;
    canCreate?: boolean;
    totalCount?: number;
}

type ExportFormat = 'csv' | 'csv_utf8' | 'json' | 'xml' | 'yaml';

export function ImportExportSidebar({
    collection,
    query = {},
    onRefresh,
    canCreate = false,
    totalCount = 0,
}: ImportExportSidebarProps) {
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Include hidden fields to show system fields like id, sort, user_created, etc. in export
    const { fields } = useFields({ collection, includeHidden: true });
    const { accessToken } = useAuthStore();

    // Import state
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [importing, setImporting] = useState(false);

    // Export state
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Export settings - initialized from current query
    const [exportFormat, setExportFormat] = useState<ExportFormat>('json');
    // Default to -1 (all items). Never inherit the page view's pagination limit.
    const [exportLimit, setExportLimit] = useState<number>(-1);
    const [exportLocation, setExportLocation] = useState<'download' | 'files'>('download');
    const [exportSort, setExportSort] = useState<string>(
        Array.isArray(query.sort) ? query.sort[0] || '' : query.sort || ''
    );
    const [exportSearch, setExportSearch] = useState<string>(query.search || '');
    const [exportFilter, setExportFilter] = useState<Filter | null>((query.filter as Filter) || null);
    const [exportFields, setExportFields] = useState<string[]>(
        Array.isArray(query.fields) ? query.fields : (query.fields ? [query.fields] : [])
    );

    // Sync export settings with current query when dialog opens.
    // Limit always resets to -1 (all items) — we never want to silently cap at the page limit.
    useEffect(() => {
        if (exportDialogOpen) {
            setExportLimit(-1);
            setExportSort(Array.isArray(query.sort) ? query.sort[0] || '' : query.sort || '');
            setExportSearch(query.search || '');
            setExportFilter((query.filter as Filter) || null);
            setExportFields(Array.isArray(query.fields) ? query.fields : (query.fields ? [query.fields] : []));
        }
    }, [exportDialogOpen, query]);

    // Error state
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [importErrors, setImportErrors] = useState<any[]>([]);

    // Calculate sort direction
    const sortDirection = useMemo(() => {
        return exportSort.startsWith('-') ? 'DESC' : 'ASC';
    }, [exportSort]);

    const sortField = useMemo(() => {
        return exportSort.startsWith('-') ? exportSort.substring(1) : exportSort;
    }, [exportSort]);

    const setSortDirection = (direction: 'ASC' | 'DESC') => {
        if (direction === 'ASC') {
            setExportSort(sortField);
        } else {
            setExportSort(`-${sortField}`);
        }
    };

    const setSortField = (field: string) => {
        if (sortDirection === 'DESC') {
            setExportSort(`-${field}`);
        } else {
            setExportSort(field);
        }
    };

    // Calculate export count
    const exportCount = useMemo(() => {
        if (exportLimit === -1 || exportLimit === null) {
            return totalCount || 0;
        }
        return Math.min(exportLimit, totalCount || 0);
    }, [exportLimit, totalCount]);

    // Get file extension for display
    const getFileExtension = (file: File) => {
        const mimeType = file.type;
        if (mimeType === 'application/json') return 'JSON';
        if (mimeType === 'text/csv' || mimeType === 'application/vnd.ms-excel') return 'CSV';
        return mimeType.split('/').pop()?.toUpperCase() || 'FILE';
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const clearFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const openFileBrowser = () => {
        fileInputRef.current?.click();
    };

    const handleImport = async () => {
        if (!selectedFile) return;

        setUploading(true);
        setImporting(false);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            // Simulate progress
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) return prev;
                    return prev + 10;
                });
            }, 100);

            // Don't set Content-Type header manually - let the browser set it with boundary
            await api.post(`/utils/import/${collection}`, formData);

            clearInterval(progressInterval);
            setUploadProgress(100);
            setImporting(true);

            // Wait a bit to show completion
            await new Promise(resolve => setTimeout(resolve, 500));

            clearFile();
            queryClient.invalidateQueries({ queryKey: ['items', collection] });
            onRefresh?.();

            toast.success('Import successful', {
                description: `Successfully imported data from ${selectedFile.name}`,
            });
        } catch (error: any) {
            const errors = error?.response?.data?.errors;
            const code = errors?.[0]?.extensions?.code;

            if (code === 'FAILED_VALIDATION' && Array.isArray(errors)) {
                setImportErrors(errors);
                setErrorDialogOpen(true);
            } else {
                toast.error('Import failed', {
                    description: error?.response?.data?.errors?.[0]?.message || 'An error occurred during import',
                });
            }
        } finally {
            setUploading(false);
            setImporting(false);
            setUploadProgress(0);
        }
    };

    const handleExportLocal = async () => {
        if (!accessToken) {
            toast.error('Authentication Error', {
                description: 'No access token found. Please log in again.',
            });
            return;
        }

        // XML / YAML — let the server handle these formats
        if (exportFormat === 'xml' || exportFormat === 'yaml') {
            const params: string[] = [`export=${exportFormat}`];
            if (exportSort) params.push(`sort=${encodeURIComponent(exportSort)}`);
            if (exportFields.length > 0) {
                for (const field of exportFields) params.push(`fields[]=${encodeURIComponent(field)}`);
            }
            if (exportSearch) params.push(`search=${encodeURIComponent(exportSearch)}`);
            if (exportFilter) params.push(`filter=${encodeURIComponent(JSON.stringify(exportFilter))}`);
            const limitValue = exportLimit !== null && exportLimit !== -1 ? exportLimit : -1;
            params.push(`limit=${limitValue}`);
            params.push(`access_token=${accessToken}`);
            const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            window.open(`${API_URL}/items/${collection}?${params.join('&')}`, '_blank');
            setExportDialogOpen(false);
            toast.success('Export started', { description: 'Download will begin shortly' });
            return;
        }

        // CSV / JSON — fetch as JSON then convert client-side.
        // This ensures dot-notation relational fields (e.g. author.name) are
        // resolved to their scalar value rather than serialised as a JSON blob.
        setExporting(true);
        try {
            const params: string[] = [];
            if (exportSort) params.push(`sort=${encodeURIComponent(exportSort)}`);
            if (exportFields.length > 0) {
                for (const field of exportFields) params.push(`fields[]=${encodeURIComponent(field)}`);
            }
            if (exportSearch) params.push(`search=${encodeURIComponent(exportSearch)}`);
            if (exportFilter) params.push(`filter=${encodeURIComponent(JSON.stringify(exportFilter))}`);
            const limitValue = exportLimit !== null && exportLimit !== -1 ? exportLimit : -1;
            params.push(`limit=${limitValue}`);

            const queryString = params.length > 0 ? `?${params.join('&')}` : '';
            const response = await api.get(`/items/${collection}${queryString}`);
            const rawData = (response as any).data;
            const items: Record<string, unknown>[] = Array.isArray(rawData) ? rawData : rawData ? [rawData] : [];

            const timestamp = new Date().toISOString().slice(0, 10);
            const baseName = `${collection}_${timestamp}`;

            if (exportFormat === 'json') {
                triggerDownload(JSON.stringify(items, null, 2), `${baseName}.json`, 'application/json');
            } else {
                // CSV / CSV UTF-8
                // Filter out wildcard entries — if the user (or the default query) had
                // fields=['*'], fall back to the keys of the first response row so that
                // the CSV has real column names instead of a literal "*" header.
                const specificFields = exportFields.filter((f) => f !== '*');
                const headers =
                    specificFields.length > 0
                        ? specificFields
                        : Object.keys(items[0] ?? {});

                const headerRow = headers.map(csvCell).join(',');
                const dataRows = items.map((item) =>
                    headers.map((h) => csvCell(getNestedValue(item, h))).join(',')
                );
                const csv = [headerRow, ...dataRows].join('\n');
                const bom = exportFormat === 'csv_utf8' ? '﻿' : '';
                triggerDownload(bom + csv, `${baseName}.csv`, 'text/csv;charset=utf-8');
            }

            setExportDialogOpen(false);
            toast.success('Export complete');
        } catch (error: any) {
            toast.error('Export failed', {
                description: error?.response?.data?.errors?.[0]?.message || error?.message || 'An error occurred',
            });
        } finally {
            setExporting(false);
        }
    };

    const handleExportToFiles = async () => {
        setExporting(true);

        try {
            await api.post(`/utils/export/${collection}`, {
                query: {
                    filter: exportFilter,
                    search: exportSearch,
                    fields: exportFields.length > 0 ? exportFields : undefined,
                    sort: exportSort ? [exportSort] : undefined,
                    limit: exportLimit,
                },
                format: exportFormat,
            });

            setExportDialogOpen(false);

            toast.success('Export started', {
                description: 'The export will be available in the Files Library when complete',
            });
        } catch (error: any) {
            toast.error('Export failed', {
                description: error?.response?.data?.errors?.[0]?.message || 'An error occurred during export',
            });
        } finally {
            setExporting(false);
        }
    };

    const handleExport = () => {
        if (exportLocation === 'download') {
            handleExportLocal();
        } else {
            handleExportToFiles();
        }
    };

    // Available fields for sort field dropdown - exclude alias fields
    const availableFields = useMemo(() => {
        return fields
            .filter((f: any) => f.type !== 'alias')
            .map((f: any) => ({
                label: f.name || f.field,
                value: f.field,
            }));
    }, [fields]);


    return (
        <div className="space-y-6 p-6">
            {/* Hidden file input */}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".json,.csv,application/json,text/csv"
                className="hidden"
            />

            {/* Import Section - Only show if canCreate */}
            {canCreate && (
                <>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <h3 className="text-sm font-semibold flex items-center gap-2">
                                <Upload className="h-4 w-4" />
                                Import from File
                            </h3>
                            <p className="text-xs text-muted-foreground">
                                Import data from a JSON or CSV file
                            </p>
                        </div>

                        {selectedFile ? (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {getFileExtension(selectedFile)} •{' '}
                                                {(selectedFile.size / 1024).toFixed(2)} KB
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={clearFile}
                                        disabled={uploading}
                                        className="flex-shrink-0 ml-2"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>

                                {uploading && (
                                    <div className="space-y-2">
                                        <Progress value={uploadProgress} className="h-2" />
                                        <p className="text-xs text-muted-foreground text-center">
                                            {importing ? 'Importing data...' : `Uploading... ${uploadProgress}%`}
                                        </p>
                                    </div>
                                )}

                                <Button
                                    onClick={handleImport}
                                    disabled={uploading}
                                    className="w-full"
                                    size="sm"
                                >
                                    <Upload className="h-4 w-4 mr-2" />
                                    {uploading ? 'Importing...' : 'Start Import'}
                                </Button>
                            </div>
                        ) : (
                            <Button
                                variant="outline"
                                onClick={openFileBrowser}
                                className="w-full"
                                size="sm"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                Choose File
                            </Button>
                        )}
                    </div>

                    <Separator />
                </>
            )}

            {/* Export Section */}
            <div className="space-y-4">
                <div className="space-y-2">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                        <Download className="h-4 w-4" />
                        Export Items
                    </h3>
                    <p className="text-xs text-muted-foreground">
                        Export current collection data
                    </p>
                </div>

                <Button
                    variant="outline"
                    onClick={() => setExportDialogOpen(true)}
                    className="w-full"
                    size="sm"
                >
                    <Download className="h-4 w-4 mr-2" />
                    Export Data
                </Button>
            </div>

            {/* Export Dialog */}
            <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
                <DialogContent className="sm:max-w-1/2 max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Export Items</DialogTitle>
                        <DialogDescription>
                            Export {exportCount.toLocaleString()} of {totalCount.toLocaleString()} items
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6">
                        {/* Format Selection */}
                        <div className="space-y-2">
                            <Label>Format</Label>
                            <Select value={exportFormat} onValueChange={(value) => setExportFormat(value as ExportFormat)}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="csv">CSV</SelectItem>
                                    <SelectItem value="csv_utf8">CSV (UTF-8 with BOM)</SelectItem>
                                    <SelectItem value="json">JSON</SelectItem>
                                    <SelectItem value="xml">XML</SelectItem>
                                    <SelectItem value="yaml">YAML</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Limit */}
                        <div className="space-y-2">
                            <Label>Limit</Label>
                            <Input
                                type="number"
                                value={exportLimit === -1 ? '' : exportLimit}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setExportLimit(val === '' ? -1 : parseInt(val));
                                }}
                                placeholder="No limit"
                            />
                            <p className="text-xs text-muted-foreground">
                                Leave empty or use -1 to export all items
                            </p>
                        </div>

                        {/* Location */}
                        <div className="space-y-2">
                            <Label>Location</Label>
                            <Select value={exportLocation} onValueChange={(value) => setExportLocation(value as 'download' | 'files')}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="download">Download to Device</SelectItem>
                                    <SelectItem value="files">Save to File Library</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Sort Field */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Sort Field</Label>
                                <Select value={sortField} onValueChange={setSortField}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select field" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {availableFields.map((field: any) => (
                                            <SelectItem key={field.value} value={field.value}>
                                                {field.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Direction</Label>
                                <Select value={sortDirection} onValueChange={(value) => setSortDirection(value as 'ASC' | 'DESC')}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ASC">Ascending</SelectItem>
                                        <SelectItem value="DESC">Descending</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="space-y-2">
                            <Label>Search</Label>
                            <Input
                                value={exportSearch}
                                onChange={(e) => setExportSearch(e.target.value)}
                                placeholder="Search items..."
                            />
                        </div>

                        {/* Filter */}
                        <div className="space-y-2">
                            <Label>Filter</Label>
                            <FilterBuilder
                                value={exportFilter}
                                fields={fields}
                                onChange={setExportFilter}
                                collection={collection}
                            />
                        </div>

                        {/* Fields Selection */}
                        <div className="space-y-2">
                            <Label>Fields to Export</Label>
                            <FieldSelector
                                collection={collection}
                                value={exportFields}
                                onChange={setExportFields}
                                allowSelectAll={true}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleExport} disabled={exporting}>
                            {exporting ? 'Exporting...' : 'Export'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Error Dialog */}
            <Dialog open={errorDialogOpen} onOpenChange={setErrorDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertCircle className="h-5 w-5" />
                            Import Errors
                        </DialogTitle>
                        <DialogDescription>
                            The following errors occurred during import:
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3">
                        {importErrors.map((error, index) => {
                            const row = error.extensions?.row;
                            const field = error.extensions?.field;
                            const value = error.extensions?.value;

                            return (
                                <div key={index} className="border rounded-lg p-4 space-y-2">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 space-y-1">
                                            <p className="text-sm font-medium">{error.message}</p>
                                            {row !== undefined && (
                                                <p className="text-xs text-muted-foreground">Row: {row + 1}</p>
                                            )}
                                            {field && (
                                                <p className="text-xs text-muted-foreground">Field: {field}</p>
                                            )}
                                            {value !== undefined && (
                                                <p className="text-xs text-muted-foreground">
                                                    Value: {JSON.stringify(value)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <DialogFooter>
                        <Button onClick={() => setErrorDialogOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
